'use client';

import { Fragment, type FC, useEffect, useMemo, useState } from 'react';
import type { ClientEntryDialogProps } from './ClientEntryDialog.types';
import { Question } from '@/_types/models/questionnaire';
import {
  Dialog,
  Flex,
  Button,
  Text,
  InlineUnsafe,
  Separator,
  Tabs,
  TabsItem,
  Alert,
} from '@/_components';
import { QuestionFormSwitch } from '../../../../QuestionFormSwitch';
import styles from './ClientEntryDialog.module.css';
export const ClientEntryDialog: FC<ClientEntryDialogProps> = ({
  currentEntryKey,
  entryQuestionsBeingUpdatedHash,
  isDisabled,
  isOpen,
  mode,
  onClose,
  specialSection,
}) => {
  const [active, setActive] = useState<number>(0);
  // Reset state to first active on close
  useEffect(() => {
    return () => {
      setActive(0);
    };
  }, []);

  // Questions of this entry
  const entryQuestionsRecord: Record<number, Question> = useMemo(
    () =>
      currentEntryKey &&
      entryQuestionsBeingUpdatedHash &&
      entryQuestionsBeingUpdatedHash[currentEntryKey]
        ? (entryQuestionsBeingUpdatedHash[
            currentEntryKey!
          ] as any as Array<Question>)
        : [],
    [currentEntryKey, entryQuestionsBeingUpdatedHash],
  );

  const entryQuestionsIds: Array<string> = Object.keys(entryQuestionsRecord);
  const entryQuestions: Array<Question> = Object.values(entryQuestionsRecord);

  const orderedEntryIds: Array<Array<string>> = useMemo(() => {
    return (
      specialSection?.sections?.map((section) => {
        const filteredSectionQuestions: Array<Question> =
          entryQuestions?.filter(
            (question) =>
              section.fields.includes(question.text) ||
              (section.name === 'General' &&
                specialSection?.sections?.every(
                  (s) => !s.fields.includes(question.text),
                )),
          );

        return filteredSectionQuestions.map((q) => q.id.toString());
      }) || []
    );
  }, [entryQuestions, specialSection?.sections]);

  const sectionGroups: Array<Array<string>> = useMemo(
    () => (!!specialSection ? orderedEntryIds : [entryQuestionsIds]),
    [orderedEntryIds, entryQuestionsIds, specialSection],
  );

  const activeSectionTabQuestionIds: Array<string> = useMemo(() => {
    if (!specialSection) return sectionGroups[active];

    const fieldOrder: Array<string> =
      specialSection.sections[active]?.fields || [];
    const currentQuestions: Array<string> = sectionGroups[active] || [];

    return currentQuestions.sort((a: string, b: string): number => {
      const qA: Question = entryQuestionsRecord[parseInt(a)];
      const qB: Question = entryQuestionsRecord[parseInt(b)];

      const idxA: number = fieldOrder.indexOf(qA.text);
      const idxB: number = fieldOrder.indexOf(qB.text);

      if (idxA === -1) return 1;
      if (idxB === -1) return -1;

      return idxA - idxB;
    });
  }, [active, specialSection, sectionGroups, entryQuestionsRecord]);

  const tabItems: Array<TabsItem> =
    specialSection?.sections?.reduce((arr: TabsItem[], section, key) => {
      const hasQuestions = sectionGroups[key]?.length > 0;
      if (!hasQuestions) return arr;

      const onClick = () => setActive(key);
      arr.push({ title: section.name, isActive: active === key, onClick });
      return arr;
    }, []) || [];

  const sectionDescription: string | undefined =
    specialSection?.sections[active]?.description;

  const renderContent: React.ReactNode = (
    <Flex
      isColumn={true}
      gap={8}
      paddingY={12}
      className={styles.clientEntryDialog__content}
    >
      {sectionDescription && <Alert type="info">{sectionDescription}</Alert>}
      {activeSectionTabQuestionIds?.map((value: string) => {
        const question: Question =
          entryQuestionsBeingUpdatedHash?.[currentEntryKey!]?.[
            parseInt(value)!
          ];

        return (
          <Fragment key={question.id}>
            <Flex isColumn={true} gap={4}>
              <Text
                type="div"
                fontWeight="semi-bold"
                data-test-id="QUESTION.TEXT"
              >
                <InlineUnsafe text={question.text} isExtremelyUnsafe={true} />
              </Text>
              {question.prior_year_value && (
                <Text type="small">
                  Prior year value: {question.prior_year_value}
                </Text>
              )}
              <QuestionFormSwitch
                question={question}
                mode={mode}
                isDisabled={isDisabled}
              />
            </Flex>
            <Separator isLight={true} />
          </Fragment>
        );
      })}
      <Flex isBetween={true} isYCentered={true}>
        <Button
          intent="secondary"
          size="small"
          onClick={onClose}
          value="Close"
        />
        <Text type="small" isItalic={true} color="success">
          Question answers are automatically saved.
        </Text>
      </Flex>
    </Flex>
  );

  return (
    <Dialog
      isOpen={isOpen}
      title="Entries"
      onClose={onClose}
      disablePadding={true}
    >
      <Dialog.Content>
        {!!tabItems?.length ? (
          <Tabs items={tabItems} isSticky={true}>
            {renderContent}
          </Tabs>
        ) : (
          renderContent
        )}
      </Dialog.Content>
    </Dialog>
  );
};
